import React, { memo, useState, useEffect, useRef } from "react";
import { message, Input } from "antd";
import debounce from "lodash/debounce";
import uniqBy from "lodash/uniqBy";

import { IItemData } from "./common.typing";
// import PageTitle from "./PageTitle";
// import IconFont from "../../_util/Iconfont";
import SvgIcon from "../../assets/icons";
import locale from "../../_util/locale";

const { TextArea } = Input;

export interface IBatchImportProps {
    prePage: string;
    onChangeCurPage: (page: string) => void;
    currentList: IItemData[];
    setCurrentList: (list: IItemData[]) => void;
    maxNum?: number;
    totalCount?: number;
    disabledids?: string[];
    batchImportApi?: (workCodes: string[]) => Promise<any>;
    /** 埋点方法 */
    track: (eventName: string, eventData?: any) => void;
    title?: string;
}

const BatchImport: React.FC<IBatchImportProps> = ({
    prePage,
    onChangeCurPage,
    currentList,
    setCurrentList,
    maxNum = 1,
    totalCount = 0,
    disabledids = [],
    batchImportApi,
    track,
    title,
}) => {
    const [isShowError, setIsShowError] = useState(false);
    const [tips, setTips] = useState("");
    const [workCodes, setWorkCodes] = useState<string[]>([]);
    const [textAreaValue, setTextAreaValue] = useState("");
    const [importLoading, setImportLoading] = useState(false);

    const isCompositionRef = useRef(false);
    const maxLen = Math.max(maxNum - totalCount, 0);
    const btnDisabled = !workCodes.length || workCodes.length > maxLen;

    /**
     * pageTitle 返回
     * prePage: home
     */
    const onBack = () => {
        onChangeCurPage(prePage);
    };

    const onComposition = e => {
        const { type } = e;

        if (type === "compositionstart") {
            isCompositionRef.current = true;
        }

        if (type === "compositionend") {
            isCompositionRef.current = false;
            onChange(e);
        }
    };

    const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
        setIsShowError(false);
        setTips("");

        let workcodeStr = e.target.value;

        if (!workcodeStr) {
            setTextAreaValue(workcodeStr);
            setWorkCodes([]);
            return;
        }

        if (isCompositionRef.current) {
            setTextAreaValue(workcodeStr);
            return;
        }

        // 输入内容不包含字母和数字
        if (/^[^\dA-Za-z]+$/.test(workcodeStr)) {
            setTextAreaValue("");
            setWorkCodes([]);
            message.error(locale("batch_import_workcode_format_error"));
            return;
        }

        const removeStr = workcodeStr.replace(/[^\da-zA-Z\n\s]/g, "");
        let splitArr = removeStr.split("\n");
        let notEmptyArr: string[] = splitArr
            .map(item => item.replace(/\s/g, ""))
            .filter(item => !!item);
        let len = notEmptyArr.length;

        if (len > maxLen) {
            setIsShowError(true);
            setTips(
                // @ts-ignore
                e?.nativeEvent?.inputType == "insertFromPaste"
                    ? locale("batch_import_paste_limit_exceed").replace(
                          "[xxx]",
                          len - maxLen
                      )
                    : locale("batch_import_input_limit_exceed")
            );

            notEmptyArr = notEmptyArr.slice(0, maxLen);

            let count = 0;
            const joinArr: string[] = [];
            for (let i = 0; i < splitArr.length; i++) {
                let item = splitArr[i];

                if (item) {
                    count++;
                }

                if (count > maxLen) {
                    break;
                }

                joinArr.push(item);
            }

            setTextAreaValue(joinArr.join("\n"));
        } else {
            if (removeStr != textAreaValue) {
                setTextAreaValue(removeStr);
            }
        }

        setWorkCodes(notEmptyArr);
    };

    const onBatchImport = debounce(
        async () => {
            if (btnDisabled) {
                return;
            }

            track("Click_Chat_Element", {
                pageName: "PSelect_001",
                chat_name: title,
                $element_name: "PSelect_004",
            });

            setImportLoading(true);

            if (!batchImportApi) {
                return;
            }

            try {
                const { success = [], fail = [] } = await batchImportApi(
                    workCodes
                );

                const failLen = fail.length;
                if (failLen) {
                    setIsShowError(true);
                    setTips(
                        locale("batch_import_error").replace("[xxx]", failLen)
                    );
                    setTextAreaValue(fail.join("\n"));
                    setWorkCodes(fail);
                } else {
                    setIsShowError(false);
                    setTips("");
                    if (success.length) {
                        setTextAreaValue("");
                        setWorkCodes([]);
                    }
                }
                if (success.length) {
                    const successList = success
                        .map(item => {
                            item.id = item.id && String(item.id);
                            item.user_id = item.id;
                            return item;
                        })
                        .filter(item => !disabledids.includes(item.id));
                    setCurrentList(
                        uniqBy([...currentList, ...successList], "id")
                    );
                }
            } finally {
                setImportLoading(false);
            }
        },
        300,
        { leading: true, trailing: false }
    );

    return (
        <div className="batchImport">
            <div className="title" onClick={onBack}>
                {/* <IconFont type="icon-arrow-left" className="iconBack" /> */}
                <SvgIcon component="ArrowLeft" className="iconBack" />
                <span className="titleName">{locale("batch_import")}</span>
            </div>

            <div className="tips">
                <p className="tipsTitle">{locale("batch_import_tips")}</p>
                <p className={`tipsSubTitle ${isShowError ? "error" : ""}`}>
                    {isShowError ? tips : locale("batch_import_tips1")}
                </p>
            </div>

            <div className="textArea">
                <TextArea
                    className={`${isShowError ? "error" : ""}`}
                    style={{
                        height: 220,
                        resize: "none",
                        boxShadow: "none",
                    }}
                    placeholder={locale("common_batch_import_placeholder")}
                    value={textAreaValue}
                    onChange={onChange}
                    onCompositionStart={onComposition}
                    onCompositionEnd={onComposition}
                    disabled={importLoading}
                />
            </div>

            <div
                className={`btn ${btnDisabled ? "disabled" : ""}`}
                onClick={onBatchImport}
            >
                {importLoading
                    ? locale("batch_importing")
                    : `${locale("batch_import_confirm")}(${workCodes.length}${
                          maxLen !== Infinity ? "/" + maxLen : ""
                      })`}
            </div>
        </div>
    );
};

export default memo(BatchImport);
